/**
 * Service for managing authentication tokens in local storage
 */

const JWT_TOKEN_KEY = "activityviz_jwt_token_v2";
const JWT_EXPIRES_KEY = "activityviz_jwt_expires_v2";
const ATHLETE_ID_KEY = "activityviz_athlete_id_v2";
export const AUTH_USER_STORAGE_KEY = "activityviz_user_v2";

const LEGACY_JWT_TOKEN_KEY = "activityviz_jwt_token";
const LEGACY_JWT_EXPIRES_KEY = "activityviz_jwt_expires";
const LEGACY_ATHLETE_ID_KEY = "activityviz_athlete_id";
const LEGACY_USER_KEY = "user";

type AuthSnapshot = {
  hasToken: boolean;
  tokenPreview: string | null;
  expiresAt: number | null;
  expiresInSeconds: number | null;
  athleteId: number | null;
  isExpired: boolean;
  isAuthenticated: boolean;
};

const parseStoredNumber = (value: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const migrateLegacyAuthStorage = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  const storage = window.localStorage;
  const migrateKey = (currentKey: string, legacyKey: string) => {
    if (!storage.getItem(currentKey)) {
      const legacyValue = storage.getItem(legacyKey);
      if (legacyValue !== null) {
        storage.setItem(currentKey, legacyValue);
      }
    }
    storage.removeItem(legacyKey);
  };

  migrateKey(JWT_TOKEN_KEY, LEGACY_JWT_TOKEN_KEY);
  migrateKey(JWT_EXPIRES_KEY, LEGACY_JWT_EXPIRES_KEY);
  migrateKey(ATHLETE_ID_KEY, LEGACY_ATHLETE_ID_KEY);
  migrateKey(AUTH_USER_STORAGE_KEY, LEGACY_USER_KEY);
};

migrateLegacyAuthStorage();

export const hasPersistedUser = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  migrateLegacyAuthStorage();
  const rawUser = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
  if (!rawUser || rawUser === "undefined") {
    return false;
  }

  try {
    return Boolean(JSON.parse(rawUser));
  } catch {
    return false;
  }
};

export const hasRestorableSession = (): boolean =>
  AuthService.isAuthenticated() && hasPersistedUser();

export const AuthService = {
  /**
   * Store JWT token and related data in localStorage
   */
  setAuth(jwtToken: string, expiresAt: number, athleteId: number): void {
    migrateLegacyAuthStorage();
    localStorage.setItem(JWT_TOKEN_KEY, jwtToken);
    localStorage.setItem(JWT_EXPIRES_KEY, expiresAt.toString());
    localStorage.setItem(ATHLETE_ID_KEY, athleteId.toString());
  },

  /**
   * Get JWT token from localStorage
   */
  getToken(): string | null {
    migrateLegacyAuthStorage();
    return localStorage.getItem(JWT_TOKEN_KEY);
  },

  /**
   * Get JWT expiration timestamp from localStorage
   */
  getExpiresAt(): number | null {
    migrateLegacyAuthStorage();
    return parseStoredNumber(localStorage.getItem(JWT_EXPIRES_KEY));
  },

  /**
   * Get athlete ID from localStorage
   */
  getAthleteId(): number | null {
    migrateLegacyAuthStorage();
    return parseStoredNumber(localStorage.getItem(ATHLETE_ID_KEY));
  },

  /**
   * Check if the current token is expired
   */
  isTokenExpired(): boolean {
    const expiresAt = this.getExpiresAt();
    if (expiresAt === null) return true;

    return Date.now() / 1000 >= expiresAt;
  },

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && !this.isTokenExpired();
  },

  /**
   * Build a debug-friendly view of the current auth state.
   */
  getSnapshot(): AuthSnapshot {
    const token = this.getToken();
    const expiresAt = this.getExpiresAt();
    const athleteId = this.getAthleteId();
    const isExpired = expiresAt === null || Date.now() / 1000 >= expiresAt;

    return {
      hasToken: Boolean(token),
      tokenPreview: token ? `${token.slice(0, 12)}...${token.slice(-6)}` : null,
      expiresAt,
      expiresInSeconds: expiresAt === null ? null : Math.floor(expiresAt - Date.now() / 1000),
      athleteId,
      isExpired,
      isAuthenticated: Boolean(token) && !isExpired,
    };
  },

  /**
   * Clear all auth data from localStorage
   */
  clearAuth(): void {
    localStorage.removeItem(JWT_TOKEN_KEY);
    localStorage.removeItem(JWT_EXPIRES_KEY);
    localStorage.removeItem(ATHLETE_ID_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
    localStorage.removeItem(LEGACY_JWT_TOKEN_KEY);
    localStorage.removeItem(LEGACY_JWT_EXPIRES_KEY);
    localStorage.removeItem(LEGACY_ATHLETE_ID_KEY);
  },
};
